import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { AnimatePresence, motion } from 'motion/react'
import { Gamepad2, Gift, Info, TrendingUp, Users, X } from 'lucide-react'

// Иконки секций по стабильному ключу key из API
const SECTION_ICONS = {
  join: Users,
  progress: TrendingUp,
  modes: Gamepad2,
  rewards: Gift
}

/**
 * Правила эвента Rush Drop — шторка снизу на мобилке, центрированная модалка на десктопе.
 * Секции и пункты приходят готовыми строками на языке пользователя —
 * выводим как есть, по фактическому массиву (секций может быть меньше четырёх).
 */
const RushDropRulesModal = ({ isOpen, onClose, rules }) => {
  const { t } = useTranslation()
  const sections = Array.isArray(rules) ? rules : []

  // Блокируем скролл body пока открыта шторка
  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [isOpen])

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 lg:items-center lg:p-6"
          onClick={onClose}
        >
          <motion.div
            initial={{ y: 60, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
            className="flex max-h-[92dvh] w-full flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-dark-primary lg:max-h-[85vh] lg:max-w-lg lg:rounded-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex shrink-0 items-center justify-between border-b border-white/10 px-5 py-4">
              <h2 className="text-lg font-bold text-white">
                {t('rushdrop.rules', 'Правила')}
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-white transition-all active:scale-95"
                aria-label={t('common.close', 'Закрыть')}
              >
                <X size={18} />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-5 py-4">
              {sections.map((section) => {
                const Icon = SECTION_ICONS[section?.key] || Info
                const items = Array.isArray(section?.items) ? section.items : []
                return (
                  <div key={section?.key || section?.title}>
                    <h3 className="mb-2 flex items-center gap-2 text-sm font-bold text-accent-primary">
                      <Icon size={16} />
                      {section?.title}
                    </h3>
                    <ul className="space-y-1.5">
                      {items.map((item, index) => (
                        <li
                          key={index}
                          className="flex items-start gap-2 text-sm leading-relaxed text-text-secondary"
                        >
                          <span className="mt-[9px] h-1 w-1 shrink-0 rounded-full bg-accent-primary/60" />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )
              })}

              {sections.length === 0 && (
                <p className="text-sm text-text-secondary">
                  {t('rushdrop.rulesEmpty', 'Правила появятся ближе к старту эвента')}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}

export default RushDropRulesModal
